import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalJobNotifications, JobNotificationEvent } from './useJobNotifications';
import { Candidate } from '../models/job';
import { jobKeys } from './useJobOffers';

interface UseTrackScoringCompletionOptions {
  jobId: string;
  enabled: boolean;
  onComplete: () => void;
  expectedCount?: number;
}

/**
 * Hook to track scoring completion via SSE events.
 * Listens for score_recomputed_success events and calls onComplete when all pending candidates are scored.
 */
export const useTrackScoringCompletion = ({
  jobId,
  enabled,
  onComplete,
  expectedCount,
}: UseTrackScoringCompletionOptions) => {
  const queryClient = useQueryClient();
  const [recomputedCount, setRecomputedCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  const expectedCountRef = useRef<number | undefined>(expectedCount);
  const recomputedCandidatesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    onCompleteRef.current = onComplete;
    expectedCountRef.current = expectedCount;
  }, [onComplete, expectedCount]);

  useEffect(() => {
    if (enabled) {
      setRecomputedCount(0);
      recomputedCandidatesRef.current.clear();
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled && expectedCountRef.current === undefined) {
      const candidates = queryClient.getQueryData<Candidate[]>(
        jobKeys.candidatesForJob(jobId)
      );
      
      if (candidates) {
        const pendingCount = candidates.filter(
          (c) => c.pipelineStageSemantic === 'pending'
        ).length;
        expectedCountRef.current = pendingCount;
      }
    }
  }, [enabled, jobId, queryClient]);

  const handleNotification = useCallback((event: JobNotificationEvent) => {
    if (event.job_id !== jobId) return;
    
    if (event.event !== 'score_recomputed_success') return;

    const fullName = event.details?.full_name as string | undefined;
    
    if (!fullName) {
      setRecomputedCount((prev) => {
        const newCount = prev + 1;
        const expected = expectedCountRef.current ?? 1;
        
        if (newCount >= expected) {
          onCompleteRef.current();
        }
        
        return newCount;
      });
      return;
    }

    if (!recomputedCandidatesRef.current.has(fullName)) {
      recomputedCandidatesRef.current.add(fullName);
      
      setRecomputedCount((prev) => {
        const newCount = prev + 1;
        const expected = expectedCountRef.current ?? 1;
        
        if (newCount >= expected) {
          onCompleteRef.current();
        }
        
        return newCount;
      });
    }
  }, [jobId]);

  useGlobalJobNotifications({
    enabled,
    onNotification: handleNotification,
  });

  useEffect(() => {
    if (!enabled) return;

    const checkComplete = () => {
      const candidates = queryClient.getQueryData<Candidate[]>(
        jobKeys.candidatesForJob(jobId)
      );

      if (!candidates) return false;

      const pendingCandidates = candidates.filter(
        (c) => c.pipelineStageSemantic === 'pending'
      );

      if (pendingCandidates.length === 0) {
        return true;
      }

      const pendingWithScores = pendingCandidates.filter(
        (c) => c.score !== null && c.score !== undefined
      );

      return pendingCandidates.length > 0 && 
             pendingWithScores.length === pendingCandidates.length && 
             recomputedCount > 0;
    };

    if (checkComplete()) {
      onCompleteRef.current();
      return;
    }

    const checkInterval = setInterval(() => {
      if (checkComplete()) {
        onCompleteRef.current();
        clearInterval(checkInterval);
      }
    }, 3000);

    return () => {
      clearInterval(checkInterval);
    };
  }, [enabled, jobId, queryClient, recomputedCount]);
};

